import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Enums from '../Enums';
import { TimeUnit } from 'Util/TimeUnit';

export interface IDynamicPriceEntityAttributes extends IModelAttributes {
	bookingType: Enums.bookingType;
	priceScaling: number;
	minimumThreshold: number;
	timeBeforeDepartureValue: number;
	timeBeforeDepartureUnit: TimeUnit;
	legacyId: number;
	staticPriceIncrease: boolean;
}
@entity('DynamicPriceEntity', 'Dynamic Price')
export default class DynamicPriceEntity extends Model implements IDynamicPriceEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * More specific booking type takes precedence
	 */
	@observable
	@attribute<DynamicPriceEntity, Enums.bookingType>()
	public bookingType: Enums.bookingType;

	/**
	 * The percentage to increase the price
	 */
	@observable
	@attribute<DynamicPriceEntity, number>()
	public priceScaling: number;

	/**
	 * The percentage of occupancy available before price scaling is applied. If null, then ignore condition.
	 */
	@observable
	@attribute<DynamicPriceEntity, number>()
	public minimumThreshold: number;

	/**
	 * The amount of time before a ferry trip departs (do not apply pricing before this point). If null, then ignore condition.
	 */
	@observable
	@attribute<DynamicPriceEntity, number>()
	public timeBeforeDepartureValue: number;

	@observable
	@attribute<DynamicPriceEntity, TimeUnit>()
	public timeBeforeDepartureUnit: TimeUnit = TimeUnit.HOURS;

	@observable
	@attribute<DynamicPriceEntity, number>()
	public legacyId: number;

	/**
	 * Whether or not the dynamic price is a static price increase or a percentage.
	 */
	@observable
	@attribute<DynamicPriceEntity, boolean>()
	public staticPriceIncrease: boolean;

	constructor(attributes?: Partial<IDynamicPriceEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IDynamicPriceEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.bookingType !== undefined) {
				this.bookingType = attributes.bookingType;
			}
			if (attributes.priceScaling !== undefined) {
				this.priceScaling = attributes.priceScaling;
			}
			if (attributes.minimumThreshold !== undefined) {
				this.minimumThreshold = attributes.minimumThreshold;
			}
			if (attributes.timeBeforeDepartureValue !== undefined) {
				this.timeBeforeDepartureValue = attributes.timeBeforeDepartureValue;
			}
			if (attributes.timeBeforeDepartureUnit !== undefined) {
				this.timeBeforeDepartureUnit = attributes.timeBeforeDepartureUnit;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.staticPriceIncrease !== undefined) {
				this.staticPriceIncrease = attributes.staticPriceIncrease;
			}
		}
	}
}
